/**
 * Achica una foto antes de subirla: lado máximo 2400 px, en JPG. Para el sitio de Phelon & Moore
 * sobra (sus recortes miden como mucho 1400 px de ancho) y deja margen para reencuadrar; así una
 * foto de cámara de 10 MB sube en un segundo y la lista de noticias carga rápido.
 *
 * Los JPG se vuelven a guardar siempre: así quedan derechos (la orientación EXIF de las fotos de
 * celular se aplica) y sin metadatos (la ubicación GPS, la cámara…). Si la foto es un PNG o WebP chico
 * y liviano, o si el navegador no puede procesarla, se sube tal cual.
 */
const MAX_SIDE = 2400;
const MAX_BYTES = 1.5 * 1024 * 1024;
const QUALITY = 0.9;

function canvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

export async function shrinkPhoto(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    // createImageBitmap respeta la orientación EXIF (las fotos de celular llegan derechas).
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
    const jpeg = file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name);
    if (scale === 1 && file.size <= MAX_BYTES && !jpeg) return file;
    const tw = Math.max(1, Math.round(width * scale));
    const th = Math.max(1, Math.round(height * scale));

    // Se reduce en pasos de a la mitad: de un solo salto (p. ej. de 7000 a 2400 px) la foto sale dentada.
    let src: CanvasImageSource = bitmap;
    let w = width;
    let h = height;
    while (w / 2 >= tw) {
      const step = canvas(Math.round(w / 2), Math.round(h / 2));
      const ctx = step.getContext('2d')!;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(src, 0, 0, step.width, step.height);
      src = step;
      w = step.width;
      h = step.height;
    }
    const out = canvas(tw, th);
    const ctx = out.getContext('2d')!;
    // El JPG no tiene transparencia: un PNG transparente queda sobre blanco (como en el sitio).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tw, th);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, tw, th);
    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/jpeg', QUALITY));
    // Un JPG chico puede crecer un poco al volver a guardarlo: se acepta mientras siga siendo liviano.
    if (!blob || blob.type !== 'image/jpeg' || blob.size > Math.max(file.size, jpeg ? MAX_BYTES : 0)) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'foto'}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
